import { useEffect, useState } from "react";
import { FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import {
  PLATFORM_TOS_CONSENT_VERSION,
  getApiErrorDetail,
  getPlatformTosStatus,
  postPlatformTosConsent,
} from "@/lib/api";

const MARINE_DEEP = "oklch(0.115 0.038 243)";
const GOLD = "oklch(0.695 0.105 68)";

const FLAG_PREFIX = "gsf:platform-tos-accepted:";

// Modale d'onboarding global (PLATFORM_TOS, low-strength).
//
// Strategie de detection en deux couches :
// 1. Fast path : flag localStorage (deja accepte dans CE navigateur).
//    Evite un round-trip a chaque session.
// 2. Slow path : si pas de flag local, on appelle GET /consents/status.
//    Couvre le cas du user qui a deja accepte ailleurs (autre navigateur,
//    localStorage clear, mode prive). Si accepted=true cote serveur, on
//    synchronise le flag local et on ne presente PAS la modale.
//
// Sans ce GET /status, le user voyait la modale, cliquait "Accepter",
// le POST violait la contrainte unique DB (consentement deja en base),
// et la modale restait bloquee sur l'erreur CONSENT_DB_ERROR_FULL.
// Le POST est desormais idempotent cote backend (PR core #10), mais
// eviter de presenter la modale est plus propre cote UX.
export function PlatformTosOnboardingDialog({ userId }: { userId: string }) {
  const [open, setOpen] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const decide = async () => {
      // 1. Fast path : flag local present = deja accepte dans ce navigateur.
      if (localStorage.getItem(FLAG_PREFIX + userId)) {
        return;
      }

      // 2. Slow path : verifier cote serveur.
      try {
        const statusResp = await getPlatformTosStatus();
        if (cancelled) return;

        if (statusResp.accepted) {
          // Synchronise le flag local pour les sessions futures.
          localStorage.setItem(
            FLAG_PREFIX + userId,
            statusResp.consented_at ?? new Date().toISOString(),
          );
          return;
        }
      } catch (err) {
        // Defense : sur erreur reseau (CORS, 5xx, timeout), on prefere
        // afficher la modale plutot que d'autoriser un acces sans preuve.
        // Le POST etant maintenant idempotent cote backend, un user qui
        // a deja consente peut cliquer sans craindre un crash.
        // eslint-disable-next-line no-console
        console.warn(
          "[ConsentCheck] Status check failed, showing modal as fallback:",
          err,
        );
      }

      if (!cancelled) {
        setOpen(true);
      }
    };

    if (userId) {
      void decide();
    }

    return () => {
      cancelled = true;
    };
  }, [userId]);

  const onConfirm = async () => {
    if (!accepted || submitting) return;
    setSubmitting(true);
    try {
      await postPlatformTosConsent();
      localStorage.setItem(FLAG_PREFIX + userId, new Date().toISOString());
      toast.success("Conditions generales acceptees.");
      setOpen(false);
    } catch (err) {
      toast.error(
        getApiErrorDetail(err) ??
          "Echec d'enregistrement du consentement. Reessayez.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent
        className="sm:max-w-lg p-0 gap-0 border-0"
        style={{
          background: MARINE_DEEP,
          border: `1px solid color-mix(in oklch, ${GOLD} 25%, transparent)`,
          color: "oklch(1 0 0)",
        }}
      >
        <DialogHeader className="px-8 pt-8 pb-3 space-y-3">
          <div
            className="w-12 h-12 flex items-center justify-center rounded-sm"
            style={{
              background: `color-mix(in oklch, ${GOLD} 15%, transparent)`,
              border: `1px solid color-mix(in oklch, ${GOLD} 40%, transparent)`,
            }}
          >
            <FileText size={20} style={{ color: GOLD }} />
          </div>
          <DialogTitle
            className="text-2xl text-left"
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              letterSpacing: "0.01em",
            }}
          >
            Conditions generales d'utilisation
          </DialogTitle>
          <DialogDescription
            className="text-left text-sm"
            style={{ color: "oklch(0.85 0.01 243)", lineHeight: 1.55 }}
          >
            Avant d'acceder aux fonctions arbitrales, prenez connaissance
            des conditions generales et de la politique de confidentialite.
            Votre acceptation est journalisee de maniere append-only
            (audit de conformite OHADA / AI Act).
          </DialogDescription>
        </DialogHeader>

        <div
          className="px-8 py-4 text-sm"
          style={{ color: "oklch(0.92 0.01 243)", lineHeight: 1.6 }}
        >
          <p className="mb-3">En cochant la case ci-dessous, vous declarez :</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>
              accepter les{" "}
              <a
                href="/mentions-legales"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: GOLD, textDecoration: "underline" }}
              >
                mentions legales
              </a>{" "}
              et la{" "}
              <a
                href="/confidentialite"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: GOLD, textDecoration: "underline" }}
              >
                politique de confidentialite
              </a>
              {" "};
            </li>
            <li>
              autoriser le traitement de vos donnees professionnelles dans
              le cadre des procedures arbitrales OHADA ;
            </li>
            <li>
              prendre acte que toutes les actions plateforme sont tracees
              a des fins probatoires.
            </li>
          </ul>

          <label className="mt-5 flex items-start gap-3 cursor-pointer">
            <Checkbox
              checked={accepted}
              onCheckedChange={(v) => setAccepted(v === true)}
              disabled={submitting}
              className="mt-0.5"
            />
            <span>
              J'accepte les conditions generales et la politique de
              confidentialite (version{" "}
              <code style={{ color: GOLD }}>
                {PLATFORM_TOS_CONSENT_VERSION}
              </code>
              ).
            </span>
          </label>
        </div>

        <DialogFooter className="px-8 py-6 flex justify-end">
          <Button
            type="button"
            onClick={onConfirm}
            disabled={!accepted || submitting}
            style={{
              background: GOLD,
              color: MARINE_DEEP,
              opacity: !accepted || submitting ? 0.6 : 1,
            }}
          >
            {submitting ? (
              <>
                <Loader2 size={14} className="animate-spin mr-2" />
                Enregistrement...
              </>
            ) : (
              "Accepter et continuer"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
